import type { Activity, WeekSummary } from '@movmania/core'
import { addDays, startOfDay, startOfWeek } from '@movmania/core'

export type Period = 'week' | 'month' | 'quarter'

/** Une barre du graphique : un jour (vue semaine) ou une semaine. */
export interface Bucket {
  key: string
  /** Libellé court sous la barre ; vide quand il n'y a pas la place. */
  label: string
  /** Libellé complet, pour la bulle et le lecteur d'écran. */
  fullLabel: string
  minutes: number
  sessions: number
  kcal: number
  isCurrent: boolean
  isFuture: boolean
}

const WEEKDAY = new Intl.DateTimeFormat('fr-FR', { weekday: 'long' })
const WEEKDAY_SHORT = new Intl.DateTimeFormat('fr-FR', { weekday: 'short' })
const DAY_MONTH = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short' })

/** Minutes, séances et kcal d'une journée. Simple somme, aucune règle métier. */
export function dayTotals(activities: readonly Activity[], day: Date) {
  const start = startOfDay(day).getTime()
  const end = addDays(startOfDay(day), 1).getTime()
  let minutes = 0
  let sessions = 0
  let kcal = 0
  for (const activity of activities) {
    const at = new Date(activity.startedAt).getTime()
    if (at < start || at >= end) continue
    minutes += activity.durationMin
    sessions += 1
    kcal += activity.kcal ?? 0
  }
  return { minutes, sessions, kcal }
}

/** Les sept jours de la semaine en cours. */
export function weekBuckets(activities: readonly Activity[], now: Date): Bucket[] {
  const monday = startOfWeek(now)
  const today = startOfDay(now).getTime()
  return Array.from({ length: 7 }, (_, offset) => {
    const day = addDays(monday, offset)
    return {
      key: day.toISOString(),
      label: WEEKDAY_SHORT.format(day).replace('.', ''),
      fullLabel: WEEKDAY.format(day),
      ...dayTotals(activities, day),
      isCurrent: day.getTime() === today,
      isFuture: day.getTime() > today,
    }
  })
}

/** Les `count` dernières semaines, lues telles quelles dans les résumés du domaine. */
export function summaryBuckets(
  summaries: readonly WeekSummary[],
  count: number,
  weekOf: (date: string) => string,
): Bucket[] {
  const slice = summaries.slice(-count)
  // Au-delà de six barres, un libellé sur deux : sinon ils se chevauchent.
  const sparse = slice.length > 6
  return slice.map((week, index) => {
    const start = new Date(week.weekStart)
    const date = DAY_MONTH.format(start).replace('.', '')
    return {
      key: week.weekKey,
      label: sparse && (slice.length - 1 - index) % 2 === 1 ? '' : `${start.getDate()}/${start.getMonth() + 1}`,
      fullLabel: weekOf(date),
      minutes: week.totalMinutes,
      sessions: week.sessions,
      kcal: week.kcal,
      isCurrent: week.isCurrent,
      isFuture: false,
    }
  })
}
